import { VFC, useMemo } from 'react';
import { isNil } from 'rambda';
import {
  Commodity,
  GasDeviceCategory,
  MembraneLayLength,
  Nullable,
  QuantometerLayLength,
  RotaryLayLength,
  ThermalLayLength,
  TurbineLayLength,
  WaterLayLength,
} from '@enmon/commons';
import { FormattedMessage, useIntl } from 'react-intl';
import { Select } from '../Select';
import { MeterDetails as messages } from '../../Form/meter_details/messages';
import { useMeterModelSuggestionsStore } from '../../../store/useMeterModelSuggestionsStore';
import { getSelectOptionsWithSuggestions } from '../utils/getSelectOptionsWithSuggestions';

type GasLengthsType =
  | typeof MembraneLayLength
  | typeof QuantometerLayLength
  | typeof RotaryLayLength
  | typeof TurbineLayLength;

// Heat/Cold is basically water
type WaterLengthsType = typeof WaterLayLength | typeof ThermalLayLength;

const getEnumValues = (lengths: GasLengthsType | WaterLengthsType) => {
  return Object.values(lengths)
    .filter(length => typeof length === 'number')
    .map((length: number) => length);
};

const getGasLayLengthNumbers = (deviceCategory?: Nullable<GasDeviceCategory>) => {
  if (!deviceCategory) return [];

  switch (deviceCategory) {
    case GasDeviceCategory.Membrane:
      return getEnumValues(MembraneLayLength);

    case GasDeviceCategory.Quantometer:
      return getEnumValues(QuantometerLayLength);

    case GasDeviceCategory.Rotary:
      return getEnumValues(RotaryLayLength);

    case GasDeviceCategory.Turbine:
      return getEnumValues(TurbineLayLength);

    default:
      console.error('Received unknown deviceCategory: ', deviceCategory);
      return [];
  }
};

const getLayLengthSelectOptions = (commodity?: Commodity, deviceCategory?: GasDeviceCategory | null): number[] => {
  switch (commodity) {
    case Commodity.Water:
      return getEnumValues(WaterLayLength);
    case Commodity.Thermal:
      return getEnumValues(ThermalLayLength);
    case Commodity.NaturalGas:
      return getGasLayLengthNumbers(deviceCategory);
    default:
      console.error('Received unknown commodity: ', commodity);
      return [];
  }
};

interface LayLengthSelectProps {
  value?: number;
  onChange?: (value: number) => void;
  commodity: Commodity;
  deviceCategory?: GasDeviceCategory | null;
}

export const LayLengthSelect: VFC<LayLengthSelectProps> = ({ deviceCategory, commodity, ...restProps }) => {
  const intl = useIntl();
  const { meterModelSuggestions } = useMeterModelSuggestionsStore();

  const layLengthOptions = useMemo(() => {
    return getSelectOptionsWithSuggestions({
      intl,
      values: getLayLengthSelectOptions(commodity, deviceCategory),
      suggestedValue: meterModelSuggestions?.layLength?.value,
      suggestedSelection: meterModelSuggestions?.layLength?.selection,
    });
  }, [intl, meterModelSuggestions, commodity, deviceCategory]);

  return (
    <Select
      allowClear
      showArrow
      className="w-100"
      disabled={commodity === Commodity.NaturalGas && isNil(deviceCategory)}
      options={layLengthOptions}
      placeholder={<FormattedMessage {...messages.layLengthPlaceholder} />}
      suffix="mm"
      {...restProps}
    />
  );
};
